import { useLayoutEffect, useState, type MouseEvent, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import './SidebarNavigation.css'

/** Lucide-style 24×24 paths, drawn at 18px to match sidebar rail */
function NavSvg({ children, size = 18 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  )
}

function PlayIcon() {
  return (
    <NavSvg>
      <polygon points="6 3 20 12 6 21 6 3" />
    </NavSvg>
  )
}

function AgentIcon() {
  return (
    <NavSvg>
      <path d="M12 8V4H8" />
      <rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="M2 14h2" />
      <path d="M20 14h2" />
      <path d="M15 13v2" />
      <path d="M9 13v2" />
    </NavSvg>
  )
}

function TestTagIcon() {
  return (
    <NavSvg>
      <path d="m3 17 2 2 4-4" />
      <path d="m3 7 2 2 4-4" />
      <path d="M13 6h8" />
      <path d="M13 12h8" />
      <path d="M13 18h8" />
    </NavSvg>
  )
}

function ProcessingIcon() {
  return (
    <NavSvg>
      <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />
    </NavSvg>
  )
}

function ResourcesIcon() {
  return (
    <NavSvg>
      <rect width="20" height="14" x="2" y="3" rx="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </NavSvg>
  )
}

function ReportsIcon() {
  return (
    <NavSvg>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 13H8" />
      <path d="M16 17H8" />
      <path d="M16 13h-2" />
    </NavSvg>
  )
}

function SettingsIcon() {
  return (
    <NavSvg>
      <path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831 2.34 2.34 0 0 1 2.33-4.033 2.34 2.34 0 0 0 3.319-1.915" />
      <circle cx="12" cy="12" r="3" />
    </NavSvg>
  )
}

function MainIcon() {
  return (
    <NavSvg>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </NavSvg>
  )
}

/** Rounded square with vertical rail — Replay / Lucide panel-left */
function PanelLeftIcon() {
  return (
    <NavSvg size={20}>
      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
      <path d="M9 3v18" />
    </NavSvg>
  )
}

const NAV_ITEMS = [
  { label: 'Main', path: '/', icon: MainIcon },
  { label: 'Runs', path: '/runs', icon: PlayIcon },
  { label: 'Agents', path: '/agents', icon: AgentIcon },
  { label: 'Test Groups', path: '/failures', icon: TestTagIcon },
  { label: 'Reports', path: '/reports', icon: ReportsIcon },
  { label: 'Resources', path: '/resources', icon: ResourcesIcon },
  { label: 'Processing', path: '/processing', icon: ProcessingIcon },
  { label: 'Settings', path: '/settings', icon: SettingsIcon },
]

function SidebarNavigation() {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)

  function isActive(path: string): boolean {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  function handleSidebarBackgroundClick(event: MouseEvent<HTMLElement>) {
    if (!collapsed) return
    const t = event.target
    const el = t instanceof Element ? t : t instanceof Node ? t.parentElement : null
    if (el?.closest('a[href]')) return
    setCollapsed(false)
  }

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-current-width',
      collapsed ? 'var(--sidebar-width-collapsed)' : 'var(--sidebar-width)',
    )
  }, [collapsed])

  return (
    <aside
      data-testid="sidebar-navigation"
      className={`sidebar-navigation ${collapsed ? 'collapsed' : ''}`}
      onClick={handleSidebarBackgroundClick}
    >
      {collapsed ? (
        <button
          type="button"
          data-testid="sidebar-expand-toggle"
          className="sidebar-logo-expand-hit"
          aria-label="Expand sidebar"
          title="Expand sidebar"
          onClick={(e) => {
            e.stopPropagation()
            setCollapsed(false)
          }}
        >
          <img src="/logo.svg" alt="" className="sidebar-logo-icon" width={34} height={40} decoding="async" />
          <span className="sidebar-logo-hover-panel" aria-hidden="true">
            <PanelLeftIcon />
          </span>
        </button>
      ) : (
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/logo.svg" alt="" className="sidebar-logo-icon" width={32} height={32} decoding="async" aria-hidden />
            <span className="sidebar-logo-text">WebDebugBench</span>
          </div>
          <button
            type="button"
            data-testid="sidebar-collapse-toggle"
            className="sidebar-panel-toggle"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            onClick={(e) => {
              e.stopPropagation()
              setCollapsed(true)
            }}
          >
            <PanelLeftIcon />
          </button>
        </div>
      )}
      <ul className="sidebar-nav-list">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon
          return (
            <li key={item.path}>
              <Link
                to={item.path}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`sidebar-nav-link ${isActive(item.path) ? 'active' : ''}`}
                aria-label={collapsed ? item.label : undefined}
              >
                {collapsed && (
                  <span className="sidebar-nav-tooltip" aria-hidden="true">
                    {item.label}
                  </span>
                )}
                <span className="sidebar-nav-icon"><Icon /></span>
                {!collapsed && <span>{item.label}</span>}
              </Link>
            </li>
          )
        })}
      </ul>
    </aside>
  )
}

export default SidebarNavigation
